import * as THREE from "three";
import { scene } from "./modules/scene";
import {
  canvas,
  debugObject,
  planeSettings,
  removeMeshFromScene,
} from "./modules/constants";
import { camera } from "./modules/camera";
import { textureLoader, getTextureDataPromise } from "./modules/textures";
import { renderer } from "./modules/renderer";
import { OrbitControls } from "three/examples/jsm/Addons.js";
import {
  ambientLight,
  directionalLight,
  directionalFillLight,
  directionalFillLightCameraHelper,
  directionalLightCameraHelper,
} from "./modules/lights";
import {} from "./modules/window";
import { gui } from "./modules/debug";
import { bakedShadowsCamera, bakedShadowsScene } from "./modules/bakedShadows";
import { setVertexPositionByColor } from "./modules/vertexPositions";
import colorVertexShader from "./shaders/color/vertex.glsl";
import colorFragmentShader from "./shaders/color/fragment.glsl";

const header = document.getElementById("header");

const axesHelper = new THREE.AxesHelper(5);
// scene.add(axesHelper);
// gui.close();
scene.background = new THREE.Color("#ded3cb");

const initPlane = () => {
  scene.clear();
  // Object
  const geometry = new THREE.PlaneGeometry(
    planeSettings.side,
    planeSettings.side,
    800,
    800
  );
  geometry.rotateX(Math.PI / 2);
  geometry.rotateY(Math.PI);

  // Change geometry
  const vertices = geometry.attributes.position.array;
  const uvs = geometry.attributes.uv.array;

  const textureDataPromise = getTextureDataPromise(debugObject.texture);

  textureDataPromise.then(
    (context) => {
      for (let i = 0, j = 0; i < vertices.length; i += 3, j += 2) {
        vertices[i + 1] = setVertexPositionByColor(
          { x: uvs[j], y: uvs[j + 1] },
          context
        );
      }
      geometry.attributes.position.needsUpdate = true;

      // Создаем текстуру для запекания
      const renderTarget = new THREE.WebGLRenderTarget(2048, 2048);
      renderTarget.texture.center.set(0.5, 0.5);
      renderTarget.texture.wrapS = THREE.RepeatWrapping;
      renderTarget.texture.repeat.x = -1;

      const bakedShadowMaterial = new THREE.MeshStandardMaterial({
        side: THREE.DoubleSide,
        shadowSide: THREE.FrontSide,
      });

      // Очищаем сцену, если до этого там уже была предыдущая текстура
      removeMeshFromScene(bakedShadowsScene);
      const bakedShadowPlane = new THREE.Mesh(geometry, bakedShadowMaterial);
      bakedShadowPlane.castShadow = true;
      bakedShadowPlane.receiveShadow = true;

      bakedShadowsScene.add(bakedShadowPlane);

      // Рендерим сцену на текстуру
      renderer.setRenderTarget(renderTarget);
      renderer.render(bakedShadowsScene, bakedShadowsCamera);
      renderer.setRenderTarget(null); // Возврат к стандартному рендеру

      // Создаем плоскость
      const material = new THREE.MeshStandardMaterial({
        color: 0xffffff,
        side: THREE.DoubleSide,
        map: context,
      });
      const texture = textureLoader.load(debugObject.texture);
      texture.colorSpace = THREE.SRGBColorSpace;

      const textureVector = textureLoader.load(debugObject.textureVector);
      textureVector.colorSpace = THREE.SRGBColorSpace;

      // Создаем плоскость
      const shaderMaterial = new THREE.RawShaderMaterial({
        vertexShader: colorVertexShader,
        fragmentShader: colorFragmentShader,
        side: THREE.DoubleSide,
        transparent: true,
        uniforms: {
          uShadowsTexture: {
            value: renderTarget.texture,
          },
          uTexture: {
            value: texture,
          },
          uTextureVector: {
            value: textureVector,
          },

          uPlaneColor: {
            value: debugObject.uPlaneColor,
          },

          uMiddleColor: {
            value: debugObject.uMiddleColor,
          },

          uDepthColor: {
            value: debugObject.uDepthColor,
          },
        },
      });
      const plane = new THREE.Mesh(geometry, shaderMaterial);
      scene.add(plane);

      // Применяем текстуру к плоскости
      material.map = context;
      material.needsUpdate = true;
    },
    (error) => {
      throw new Error(error);
    }
  );
};

initPlane();

// Lights
scene.add(ambientLight);
scene.add(directionalLight);
scene.add(directionalLightCameraHelper);
scene.add(directionalFillLight);
scene.add(directionalFillLightCameraHelper);

// Camera
camera.lookAt(new THREE.Vector3(0, 0, 0));
scene.add(camera);

const colorTweaks = gui.addFolder("Colors");

colorTweaks
  .addColor(debugObject, "uPlaneColor")
  .onChange(() => {
    material.color.set(debugObject.uPlaneColor);
  })
  .name("Background color")
  .hide();

colorTweaks.addColor(debugObject, "uMiddleColor").name("Top color").listen();
colorTweaks.addColor(debugObject, "uDepthColor").name("Depth color").listen();

const mapTweaks = gui.addFolder("Maps");

mapTweaks
  .add(debugObject, "texture", ["Japan", "Madagascar", "Peru", "Bulgaria"])
  .onChange((value) => {
    switch (value) {
      case "Peru":
        debugObject.texture = "./textures/peru.jpg";
        debugObject.textureVector = "./textures/peru_vector.jpg";
        debugObject.uMiddleColor = new THREE.Color("#178e9e");
        debugObject.uDepthColor = new THREE.Color("#b0e5ec");
        header.innerHTML = "Peru";
        initPlane();
        break;
      case "Madagascar":
        debugObject.texture = "./textures/madagascar.jpg";
        debugObject.textureVector = "./textures/madagascar_vector.jpg";
        debugObject.uMiddleColor = new THREE.Color("#c6e571");
        debugObject.uDepthColor = new THREE.Color("#4b7531");
        header.innerHTML = "Madagascar";
        initPlane();
        break;
      case "Bulgaria":
        debugObject.texture = "./textures/bulgaria.jpg";
        debugObject.textureVector = "./textures/bulgaria_vector.jpg";
        debugObject.uDepthColor = new THREE.Color("#f5db56");
        debugObject.uMiddleColor = new THREE.Color("#9e4700");
        header.innerHTML = "Bulgaria";
        initPlane();
        break;
      case "Japan":
        debugObject.texture = "./textures/japan_polygons_small.png";
        debugObject.textureVector = "./textures/japan_vector_stroke.jpg";
        debugObject.uMiddleColor = new THREE.Color("#aca6ce");
        debugObject.uDepthColor = new THREE.Color("#736495");
        header.innerHTML = "Japan";
        initPlane();
        break;
      default:
    }
  });

// Данные

// const cylinderGeometry = new THREE.CylinderGeometry(0.003, 0.004, 0.2);
// const cylinderMaterial = new THREE.MeshBasicMaterial({ color: 0xff0000 });
// const tokio = new THREE.Mesh(cylinderGeometry, cylinderMaterial);
// tokio.position.set(0.455, 0.1, 0.21);
// scene.add(tokio);

// const yokogama = new THREE.Mesh(cylinderGeometry, cylinderMaterial);
// yokogama.position.set(0.45, 0.1, 0.23);
// scene.add(yokogama);

// Controls
const controls = new OrbitControls(camera, canvas);
controls.enableDamping = true;

const tick = () => {
  renderer.render(scene, camera);
  window.requestAnimationFrame(tick);
};
tick();
